/**
 * Puzzle `?view=` values: feed pagination (`page1`, `page2`, …) and listing UI modes.
 */

export const PUZZLE_VIEW_PARAM = "view" as const;

/** Feed infinite-scroll routes (`/` homepage or legacy `/puzzle`). */
export function isPuzzleFeedPath(pathname: string): boolean {
    return pathname === "/" || pathname === "/puzzle";
}

export type PuzzleListingView =
    | "listing"
    | "scoreboard"
    | "vertical"
    | "full_screen";

const PUZZLE_LISTING_VIEW_VALUES: ReadonlySet<string> = new Set([
    "listing",
    "scoreboard",
    "vertical",
    "full_screen",
]);

/** Legacy tab hashes migrated to `?view=`. */
const LEGACY_UI_HASHES: ReadonlySet<string> = new Set(["", "scoreboard", "guesses"]);

const PUZZLE_FEED_PAGE_VIEW_PATTERN = /^page\d+$/i;

export function buildPuzzleFeedPageView(pageNumber: number): string {
    const safePage = Math.max(1, Math.floor(pageNumber));
    return `page${safePage}`;
}

export function isPuzzleFeedPageView(value: string | null | undefined): boolean {
    return value != null && PUZZLE_FEED_PAGE_VIEW_PATTERN.test(value);
}

export function isPuzzleListingView(value: string | null | undefined): value is PuzzleListingView {
    return value != null && PUZZLE_LISTING_VIEW_VALUES.has(value);
}

export function parsePuzzleListingViewFromSearch(search: string): PuzzleListingView | null {
    const raw = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get(PUZZLE_VIEW_PARAM);
    if (!raw || isPuzzleFeedPageView(raw)) {
        return null;
    }
    return isPuzzleListingView(raw) ? raw : null;
}

export function parsePuzzleViewFromLocation(location: {
    search: string;
    hash: string;
}): PuzzleListingView {
    const fromSearch = parsePuzzleListingViewFromSearch(location.search);
    if (fromSearch) {
        return fromSearch;
    }
    const hashRaw = location.hash.replace(/^#/, "").toLowerCase();
    if (hashRaw === "scoreboard") {
        return "scoreboard";
    }
    return "listing";
}

export function buildPuzzleViewSearchParams(
    currentSearch: string,
    view: PuzzleListingView,
    options?: { preserveOtherParams?: boolean },
): string {
    const preserve = options?.preserveOtherParams !== false;
    const params = preserve
        ? new URLSearchParams(currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch)
        : new URLSearchParams();
    params.set(PUZZLE_VIEW_PARAM, view);
    const serialized = params.toString();
    return serialized ? `?${serialized}` : "";
}

export function isLegacyPuzzleUiHash(hashRaw: string): boolean {
    return LEGACY_UI_HASHES.has(hashRaw.toLowerCase());
}

/** Removes legacy UI hashes (#scoreboard, #guesses) from a puzzle path. */
export function stripLegacyPuzzleUiHashFromPath(path: string): string {
    const hashIndex = path.indexOf("#");
    if (hashIndex < 0) {
        return path;
    }
    const hashRaw = path.slice(hashIndex + 1);
    if (!isLegacyPuzzleUiHash(hashRaw)) {
        return path;
    }
    return path.slice(0, hashIndex);
}

/** Strip feed `pageN` (and any prior `view`) when opening a puzzle listing. */
export function shouldStripSearchParamOnPuzzleListingNavigation(key: string): boolean {
    return key === PUZZLE_VIEW_PARAM;
}

export function normalizeVariantLinkKey(link: string): string {
    try {
        return decodeURIComponent(link);
    } catch {
        return link;
    }
}

export function hasExplicitPuzzleListingViewInSearch(search: string): boolean {
    return parsePuzzleListingViewFromSearch(search) != null;
}

export function appendPuzzleViewToPath(path: string, view: PuzzleListingView): string {
    const stripped = stripLegacyPuzzleUiHashFromPath(path);
    const hashIndex = stripped.indexOf("#");
    const beforeHash = hashIndex >= 0 ? stripped.slice(0, hashIndex) : stripped;
    const hash = hashIndex >= 0 ? stripped.slice(hashIndex) : "";
    const searchIndex = beforeHash.indexOf("?");
    const basePath = searchIndex >= 0 ? beforeHash.slice(0, searchIndex) : beforeHash;
    const search = searchIndex >= 0 ? beforeHash.slice(searchIndex) : "";
    const nextSearch = buildPuzzleViewSearchParams(search, view);
    return `${basePath}${nextSearch}${hash}`;
}
